import type { LandingContent } from "./types";

export const zh: LandingContent = {
  locale: "zh",
  seo: {
    title: "Prismark 专业相机 · 轻量直观的 iOS 手动相机工具",
    description: "Prismark 是一款轻量而专业的 iOS 相机应用，支持手动曝光与对焦调节、实时直方图与斑马纹、自定义 LUT 导入、双摄画中画录制与 HDR 视频。"
  },
  nav: {
    features: "功能",
    pro: "Prismark Pro",
    dualCamera: "双摄",
    screens: "界面",
    download: "下载",
    privacy: "隐私政策",
    support: "支持",
    language: "EN"
  },
  hero: {
    tag: "iOS 专业相机",
    title: "Prismark",
    headline: "清晰专注的取景器，直观顺手的手动控制。",
    body: "支持 EV、快门、ISO、对焦与白平衡精确微调，配备实时直方图与斑马纹过曝提示，完全基于 iOS 原生相机框架构建。",
    primaryCta: "下载",
    secondaryCta: "了解功能"
  },
  overview: {
    title: "覆盖日常拍摄的核心功能",
    subtitle: "拍照、录像、双摄画中画与快捷回看，配合直观的手动参数与辅助测光工具，操作自然顺手。",
    features: [
      { title: "照片拍摄", body: "快速响应的快门与取景器，支持 EV、快门速度、ISO、手动对焦与白平衡调节。" },
      { title: "视频录制", body: "支持 4K 与 60fps 规格，在兼容机型上提供 10-bit HDR 录制与实时状态监控。" },
      { title: "双摄录制", body: "在支持的设备上同时记录眼前景物与人物反应，画中画窗口支持拖动调整位置。", badge: "Prismark Pro" },
      { title: "快捷回看", body: "拍摄后直接查看完整 EXIF 参数、色彩格式与分辨率，无需离开拍摄界面。" }
    ]
  },
  camera: {
    title: "取景器与手动参数控制",
    body: "底栏整合了 EV、快门速度、ISO、手动对焦与色温白平衡。配合实时亮度直方图与斑马纹过曝提示，拍摄前就能准确掌握曝光细节。",
    points: ["网格线与水平仪", "实时亮度直方图", "斑马纹过曝提示", "点击对焦与 AE/AF 锁定", "内置滤镜支持调节强度", "参数工具栏按需展开"]
  },
  video: {
    title: "稳定清晰的视频录制",
    body: "视频模式下保留清晰的参数状态反馈与取景构图线。在兼容设备上支持录制 10-bit HDR 视频，录制计时与状态提示一目了然。",
    points: ["兼容机型支持 10-bit HDR 视频录制", "录制状态指示与精确计时", "常用变焦档位快速切换", "视频录制补光灯控制"]
  },
  dual: {
    title: "前后镜头双摄录制",
    body: "在支持的设备上，双摄录制可同步捕捉眼前画面与人物反应。画中画窗口支持轻触拖动位置，非常适合 Vlog、讲解演示与旅行记录。",
    points: ["前后镜头同步收录画面与反应", "画中画窗口支持自由拖动位置", "生成单条封装完整的 10-bit HDR 素材", "无需后期多机位拼合"],
    badge: "Prismark Pro"
  },
  gallery: {
    title: "拍摄后就地回看",
    body: "轻点缩略图即可预览刚拍好的照片与视频，查看完整的 EXIF 拍摄参数、镜头焦段与文件分辨率，支持直接流畅播放。"
  },
  pro: {
    eyebrow: "一次买断",
    title: "Prismark Pro",
    body:
      "其中三项进阶功能属于 Prismark Pro，一次购买永久解锁。其余功能都已包含在内，不需要再付任何费用。这是一次性买断，不是订阅，没有周期扣款，也不需要手动取消。",
    items: [
      { title: "导入自定义 LUT", body: "支持 .cube 与 Hald PNG 文件，导入后与内置滤镜并列排布，同样支持滑动调节滤镜强度。" },
      { title: "ProRAW 与 RAW 输出", body: "在支持的机型上保存原始传感器数据，为后期修图与调色保留更充裕的动态范围。" },
      { title: "双摄画中画", body: "两颗镜头同时录制，在一条 10-bit HDR 视频素材中完整记录环境画面与你的反应。" }
    ],
    note:
      "其他功能都不额外收费：手动曝光参数、内置滤镜、4K 60fps 录制、HDR 视频、照片水印以及构图辅助线。如果你在 Prismark Pro 推出前就已下载过应用，这三项功能保持对你免费解锁。"
  },
  screens: {
    title: "简洁直接的拍摄界面",
    subtitle: "从照片取景、视频录制、双摄画中画到相册回看，所有操作都在连贯的取景界面中完成。",
    labels: ["拍照", "录像", "双摄", "相册"]
  },
  philosophy: {
    title: "界面与交互细节",
    cards: [
      { title: "原生框架", body: "基于 iOS 原生相机框架开发，启动迅速，不嵌入第三方分析或广告 SDK，拍摄体验可靠稳定。" },
      { title: "按需呼出参数", body: "取景器默认只保留构图辅助信息。需要调节曝光或对焦时轻点滑块展开，不遮挡主体画面。" },
      { title: "系统级交互", body: "完整支持深色与浅色外观、触感震动反馈与双指捏合缩放手势，贴合原生 iOS 操作直觉。" }
    ]
  },
  cta: {
    title: "在 App Store 获取 Prismark",
    body: "支持 iPhone 与 iPad。Prismark Pro 为可选的一次性内购，其余功能不额外收费。",
    label: "在 App Store 下载"
  },
  footer: {
    tagline: "轻量而专业的 iOS 相机工具。",
    privacy: "隐私政策",
    terms: "用户协议",
    support: "支持",
    contact: "联系",
    productBy: "Axqora 出品。",
    icp: "粤ICP备2021054614号",
    icpHref: "https://beian.miit.gov.cn/"
  }
};
